import type { FC } from 'react';
import { Fragment } from 'react';

import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Chip } from '@packages/shared/src/ui/Chip/Chip';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { FilterDetailsLoaderFragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterDetailsLoaderFragmentFragmentDoc';

/* GraphQL */ `
  fragment FilterDetailsLoaderFragment on SearchFilterBase {
    __typename
    ... on SearchDefaultFilter {
      displayMode
      isSearchable
    }
    ... on SearchRangeFilter {
      histogram
    }
  }
`;

export type FilterDetailsLoaderProps = {
  maskedData: FragmentType<typeof FilterDetailsLoaderFragmentFragmentDoc>;
};

const ListFilterLoader = ({ isSearchable }: { isSearchable?: boolean | null }) => (
  <div data-testid="list-loader">
    {isSearchable && (
      <Skeleton data-testid="searchbox-skeleton" className="h-[90px] w-full rounded-none" />
    )}
    {Array.from({ length: 3 }, (_, index) => (
      <Fragment key={`listitem-${index}`}>
        <div className="flex w-full items-center gap-2 p-1">
          <Skeleton className="h-2.5 w-5 rounded-none" />
          <Skeleton className="h-1.5 w-full rounded-sm" />
          <Chip variant="filled" className="border-none">
            <Skeleton className="mx-1 h-1.5 w-2.5 rounded-sm" />
          </Chip>
        </div>
        <Divider flexItem />
      </Fragment>
    ))}
  </div>
);

const PatchFilterLoader = () => (
  <div data-testid="patch-loader" className="p-1.5">
    <div className="pb-1">
      <Skeleton className="h-3 w-full rounded-sm" />
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-[60px] w-[60px] rounded-none" />
      <Skeleton className="h-[60px] w-[60px] rounded-none" />
      <Skeleton className="h-[60px] w-[60px] rounded-none" />
      <Skeleton className="h-[60px] w-[60px] rounded-none" />
    </div>
  </div>
);

const SliderFilterLoader = ({ hasHistogram }: { hasHistogram: boolean }) => (
  <div data-testid="slider-loader" className="p-1.5">
    <div className="pb-2">
      <Skeleton className="h-3 w-full rounded-sm" />
    </div>
    <div className="flex justify-between gap-2">
      <Chip variant="filled" className="border-none">
        <Skeleton className="mx-1 h-1.5 w-2.5 rounded-sm" />
      </Chip>
      <Chip variant="filled" className="border-none">
        <Skeleton className="mx-1 h-1.5 w-2.5 rounded-sm" />
      </Chip>
    </div>
    <div className="mx-3 my-1">
      <Skeleton className={`${hasHistogram ? 'h-[130px]' : 'h-[45px]'} w-full rounded-none`} />
    </div>
    <div className="flex justify-between">
      <Skeleton className="h-1.5 w-[100px] rounded-sm" />
      <Skeleton className="h-1.5 w-[100px] rounded-sm" />
    </div>
  </div>
);

/**
 * Provides a loading state for the filter details
 * */
export const FilterDetailsLoader: FC<FilterDetailsLoaderProps> = ({ maskedData }) => {
  const filter = unmask<typeof FilterDetailsLoaderFragmentFragmentDoc>(maskedData);

  if ('displayMode' in filter) {
    switch (filter.displayMode) {
      case 'LIST':
        return <ListFilterLoader isSearchable={filter.isSearchable} />;
      case 'GRID':
        return <PatchFilterLoader />;
      default:
        return null;
    }
  } else if (filter.__typename === 'SearchRangeFilter') {
    // TODO: add histogram after it is added to the schema in the resolver
    return <SliderFilterLoader hasHistogram={!!filter.histogram} />;
  }

  return null;
};
